import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE } from '../../utils/api'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts'

export default function AdminClassifiedDashboard() {
    const { token } = useAuth();
    const [stats, setStats] = useState({
        ownerProperties: 0,
        brokerProperties: 0,
        brokerRequirements: 0
    });
    const [loading, setLoading] = useState(true);

    const chartData = [
        { name: 'Wed', count: 280 },
        { name: 'Thu', count: 219 },
        { name: 'Fri', count: 130 },
        { name: 'Sat', count: 130 },
        { name: 'Sun', count: 65 },
        { name: 'Mon', count: 210 },
        { name: 'Tue', count: 155 },
    ];

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!token) return;
            try {
                const [propRes, leadRes] = await Promise.all([
                    fetch(`${API_BASE}/classified-properties`, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(`${API_BASE}/leads`, { headers: { Authorization: `Bearer ${token}` } })
                ]);

                const properties = await propRes.json();
                const leads = await leadRes.json();

                const ownerProps = Array.isArray(properties) ? properties.filter((p: any) => 
                    (p.postedBy || '').toLowerCase() === 'owner' || 
                    ['owner', 'admin'].includes((p.owner?.role || '').toLowerCase())
                ).length : 0;

                const brokerProps = Array.isArray(properties) ? properties.filter((p: any) => 
                    ['broker', 'agent'].includes((p.postedBy || '').toLowerCase()) || 
                    ['broker', 'agent'].includes((p.owner?.role || '').toLowerCase())
                ).length : 0;

                const requirements = Array.isArray(leads) ? leads.filter((l: any) => 
                    l.source === 'Requirement' || (l.status === 'Requirement')
                ).length : 0;

                setStats({
                    ownerProperties: ownerProps,
                    brokerProperties: brokerProps,
                    brokerRequirements: requirements
                });
            } catch (err) {
                console.error('Error fetching classified dashboard data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [token]);

    return (
        <AdminLayout>
            <div className="mb-10">
                <h1 className="text-2xl font-bold text-slate-800 mb-1">Classifieds Overview</h1>
                <p className="text-slate-500 text-sm">Real-time property and requirement metrics.</p>
            </div>

            {/* Stats Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Owner Properties */}
                <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-50 flex items-center justify-between group">
                    <div>
                        <p className="text-slate-400 text-sm font-bold mb-1">Owner<br/>Properties</p>
                        <h3 className="text-2xl font-black text-slate-800">{loading ? '...' : stats.ownerProperties.toLocaleString()}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
                        <i className="fa-solid fa-house text-xl"></i>
                    </div>
                </div>

                {/* Broker Properties */}
                <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-50 flex items-center justify-between group">
                    <div>
                        <p className="text-slate-400 text-sm font-bold mb-1">Broker<br/>Properties</p>
                        <h3 className="text-2xl font-black text-slate-800">{loading ? '...' : stats.brokerProperties.toLocaleString()}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center text-orange-500 group-hover:scale-110 transition-transform">
                        <i className="fa-solid fa-briefcase text-xl"></i>
                    </div>
                </div>

                {/* Broker Requirements */}
                <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-50 flex items-center justify-between group">
                    <div>
                        <p className="text-slate-400 text-sm font-bold mb-1">Broker<br/>Requirements</p>
                        <h3 className="text-2xl font-black text-slate-800">{loading ? '...' : stats.brokerRequirements.toLocaleString()}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 group-hover:scale-110 transition-transform">
                        <i className="fa-solid fa-clipboard-list text-xl"></i>
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-50 mb-10">
                <h4 className="text-sm font-black text-slate-800 mb-8 uppercase tracking-tight">Property Activity (Last 7 Days)</h4>
                <div className="h-[350px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis 
                                dataKey="name" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{fill: '#94a3b8', fontSize: 12, fontWeight: 700}} 
                                dy={10} 
                            />
                            <YAxis 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{fill: '#94a3b8', fontSize: 12, fontWeight: 700}} 
                                domain={[0, 260]}
                                ticks={[0, 65, 130, 195, 260]}
                            />
                            <Tooltip 
                                cursor={{fill: '#f8fafc'}}
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        return (
                                            <div className="bg-white p-4 shadow-2xl rounded-xl border border-slate-50">
                                                <p className="text-sm font-bold text-slate-800 mb-1">{label}</p>
                                                <p className="text-sm font-black text-purple-600">count : {payload[0].value}</p>
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Bar 
                                dataKey="count" 
                                fill="#7c3aed" 
                                radius={[6, 6, 0, 0]} 
                                barSize={45}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={index === 1 ? '#7c3aed' : '#7c3aed'} fillOpacity={index === 1 ? 1 : 0.8} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </AdminLayout>
    )
}
